import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Receipt, Download, Upload, Trash2, Check, X, DollarSign, FileText, Users, Wrench } from 'lucide-react';
import api from '../lib/api';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';

const EXPENSE_CATEGORIES = [
  { value: 'carburante', label: 'Carburante' },
  { value: 'parcheggio', label: 'Parcheggio' },
  { value: 'pedaggi', label: 'Pedaggi' },
  { value: 'pasti', label: 'Pasti' },
  { value: 'alloggio', label: 'Alloggio' },
  { value: 'materiali', label: 'Materiali' },
  { value: 'attrezzature', label: 'Attrezzature' },
  { value: 'trasporti', label: 'Trasporti' },
  { value: 'altro', label: 'Altro' },
];

const MONTHS = [
  { value: 1, label: 'Gennaio' }, { value: 2, label: 'Febbraio' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' }, { value: 5, label: 'Maggio' }, { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Dicembre' },
];

export default function ExpenseClaimsPage() {
  const { isRole, user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterEmployee, setFilterEmployee] = useState('__all__');
  const [filterStatus, setFilterStatus] = useState('__all__');
  
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [showReject, setShowReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  const [form, setForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: 'altro',
    description: '',
    receipt_number: '',
    site_id: '',
    notes: ''
  });

  const load = async () => {
    try {
      const params = { year: filterYear, month: filterMonth };
      if (filterEmployee !== '__all__') params.employee_id = filterEmployee;
      if (filterStatus !== '__all__') params.status = filterStatus;
      
      const [claimsRes, empRes, sitesRes] = await Promise.all([
        api.get('/expense-claims', { params }),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/sites').catch(() => ({ data: [] })),
      ]);
      setClaims(claimsRes.data);
      setEmployees(empRes.data);
      setSites(sitesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterYear, filterMonth, filterEmployee, filterStatus]);

  const handleCreate = async () => {
    try {
      const data = {
        ...form,
        amount: parseFloat(form.amount) || 0
      };
      if (!data.employee_id && isRole('dipendente')) {
        delete data.employee_id; // Backend will use current user's employee
      }
      const res = await api.post('/expense-claims', data);
      setShowCreate(false);
      setForm({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        category: 'altro',
        description: '',
        receipt_number: '',
        site_id: '',
        notes: ''
      });
      // Show upload dialog for the new claim
      setShowUpload(res.data.id);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleApprove = async (claimId) => {
    try {
      await api.put(`/expense-claims/${claimId}/approve`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleReject = async () => {
    if (!showReject) return;
    try {
      await api.put(`/expense-claims/${showReject}/reject`, { reason: rejectReason });
      setShowReject(null);
      setRejectReason('');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleReimburse = async (claimId) => {
    try {
      await api.put(`/expense-claims/${claimId}/reimburse`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (claimId) => {
    if (!window.confirm('Eliminare questa richiesta?')) return;
    try {
      await api.delete(`/expense-claims/${claimId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUpload = async (claimId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/expense-claims/${claimId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowUpload(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    }
  };

  const handleExport = async (type) => {
    try {
      let url = '';
      let filename = '';
      
      switch (type) {
        case 'expenses':
          url = `/export/payroll?year=${filterYear}&month=${filterMonth}`;
          filename = `rimborsi_spese_${filterYear}_${String(filterMonth).padStart(2, '0')}.csv`;
          break;
        case 'attendance':
          url = `/export/attendance?year=${filterYear}&month=${filterMonth}`;
          filename = `presenze_${filterYear}_${String(filterMonth).padStart(2, '0')}.csv`;
          break;
        case 'payroll':
          url = `/export/payroll-summary?year=${filterYear}&month=${filterMonth}`;
          filename = `riepilogo_buste_paga_${filterYear}_${String(filterMonth).padStart(2, '0')}.csv`;
          break;
        case 'employees':
          url = '/export/employees';
          filename = `dipendenti_${new Date().toISOString().split('T')[0]}.csv`;
          break;
        case 'equipment':
          url = '/export/equipment';
          filename = `attrezzature_${new Date().toISOString().split('T')[0]}.csv`;
          break;
        default:
          return;
      }
      
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (err) {
      alert('Errore durante l\'esportazione');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const statusBadge = (status) => {
    const styles = {
      in_attesa: 'bg-amber-50 text-amber-700 border-amber-200',
      approvato: 'bg-blue-50 text-blue-700 border-blue-200',
      rifiutato: 'bg-red-50 text-red-700 border-red-200',
      rimborsato: 'bg-green-50 text-green-700 border-green-200',
    };
    const labels = {
      in_attesa: 'In Attesa',
      approvato: 'Approvato',
      rifiutato: 'Rifiutato',
      rimborsato: 'Rimborsato',
    };
    return <Badge variant="outline" className={styles[status] || styles.in_attesa}>{labels[status] || status}</Badge>;
  };

  // Calculate totals
  const totals = claims.reduce((acc, c) => {
    if (c.status === 'approvato' || c.status === 'rimborsato') {
      acc.approved += c.amount || 0;
    }
    if (c.status === 'in_attesa') {
      acc.pending += c.amount || 0;
    }
    acc.total += c.amount || 0;
    return acc;
  }, { total: 0, approved: 0, pending: 0 });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expense-claims-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Rimborsi Spese</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(parseInt(v))}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[currentYear, currentYear - 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(filterMonth)} onValueChange={(v) => setFilterMonth(parseInt(v))}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {isRole('admin', 'aspp', 'qhse') && (
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Dipendente" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              <SelectItem value="in_attesa">In Attesa</SelectItem>
              <SelectItem value="approvato">Approvato</SelectItem>
              <SelectItem value="rimborsato">Rimborsato</SelectItem>
              <SelectItem value="rifiutato">Rifiutato</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setShowCreate(true)} data-testid="add-expense-btn">
            <Plus size={16} className="mr-1" /> Nuova Spesa
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-amber-600 mb-1">In Attesa</p>
            <p className="text-2xl font-bold text-amber-700">{formatCurrency(totals.pending)}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-green-600 mb-1">Approvati/Rimborsati</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(totals.approved)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-slate-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-slate-600 mb-1">Totale Richieste</p>
            <p className="text-2xl font-bold text-slate-700">{formatCurrency(totals.total)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="richieste">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="richieste">Richieste ({claims.length})</TabsTrigger>
          <TabsTrigger value="esportazioni">Esportazioni</TabsTrigger>
        </TabsList>

        <TabsContent value="richieste">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    {isRole('admin', 'aspp', 'qhse') && <TableHead>Dipendente</TableHead>}
                    <TableHead>Categoria</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="hidden md:table-cell">Scontrino</TableHead>
                    <TableHead className="w-28">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {claims.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        Nessuna richiesta per questo periodo
                      </TableCell>
                    </TableRow>
                  ) : claims.map(claim => (
                    <TableRow key={claim.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm">{claim.date}</TableCell>
                      {isRole('admin', 'aspp', 'qhse') && <TableCell className="text-sm font-medium">{claim.employee_name}</TableCell>}
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-100 text-slate-600 text-xs">
                          {EXPENSE_CATEGORIES.find(c => c.value === claim.category)?.label || claim.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{claim.description}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(claim.amount)}</TableCell>
                      <TableCell>{statusBadge(claim.status)}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {claim.file_url ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${claim.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1">
                            <Download size={14} /> Vedi
                          </a>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setShowUpload(claim.id)} className="text-xs p-1 h-auto">
                            <Upload size={14} />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {claim.status === 'in_attesa' && isRole('admin', 'aspp', 'qhse') && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleApprove(claim.id)} className="text-green-600 hover:text-green-800 p-1 h-auto" title="Approva">
                                <Check size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setShowReject(claim.id)} className="text-red-600 hover:text-red-800 p-1 h-auto" title="Rifiuta">
                                <X size={14} />
                              </Button>
                            </>
                          )}
                          {claim.status === 'approvato' && isRole('admin') && (
                            <Button variant="ghost" size="sm" onClick={() => handleReimburse(claim.id)} className="text-green-600 hover:text-green-800 p-1 h-auto" title="Segna Rimborsato">
                              <DollarSign size={14} />
                            </Button>
                          )}
                          {(claim.status === 'in_attesa' || isRole('admin')) && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(claim.id)} className="text-red-600 hover:text-red-800 p-1 h-auto" title="Elimina">
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="esportazioni">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <Card className="border-slate-200 hover:border-blue-300 cursor-pointer transition-colors" onClick={() => handleExport('payroll')}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Riepilogo Buste Paga</h3>
                  <p className="text-sm text-slate-500">Ore + Rimborsi per dipendente</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200 hover:border-green-300 cursor-pointer transition-colors" onClick={() => handleExport('attendance')}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <Receipt size={24} className="text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Presenze Mensili</h3>
                  <p className="text-sm text-slate-500">Ore lavorate per dipendente</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200 hover:border-amber-300 cursor-pointer transition-colors" onClick={() => handleExport('expenses')}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <DollarSign size={24} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Rimborsi Spese</h3>
                  <p className="text-sm text-slate-500">Dettaglio scontrini approvati</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200 hover:border-purple-300 cursor-pointer transition-colors" onClick={() => handleExport('employees')}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Users size={24} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Lista Dipendenti</h3>
                  <p className="text-sm text-slate-500">Anagrafica completa</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200 hover:border-orange-300 cursor-pointer transition-colors" onClick={() => handleExport('equipment')}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Wrench size={24} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Lista Attrezzature</h3>
                  <p className="text-sm text-slate-500">Inventario completo</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuova Richiesta Rimborso</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {isRole('admin', 'aspp', 'qhse') && (
              <div className="col-span-2 space-y-1.5">
                <Label>Dipendente *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Data *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Importo (EUR) *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="expense-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>N. Scontrino</Label>
              <Input value={form.receipt_number} onChange={(e) => setForm({ ...form, receipt_number: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Descrizione *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="es. Pranzo con cliente" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Cantiere (opzionale)</Label>
              <Select value={form.site_id || '__none__'} onValueChange={(v) => setForm({ ...form, site_id: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuno</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Note</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.amount || !form.description || (!form.employee_id && isRole('admin', 'aspp', 'qhse'))} data-testid="expense-save-btn">
              Salva e Carica Scontrino
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={!!showUpload} onOpenChange={(o) => { if (!o) { setShowUpload(null); setPendingFile(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Carica Scontrino</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-2">Carica una foto dello scontrino o della ricevuta e controlla l'anteprima prima di salvare.</p>
          <div className="space-y-3">
            <Input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
            />
            <FilePreview file={pendingFile} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUpload(null); setPendingFile(null); }}>Salta</Button>
            <Button onClick={() => { handleUpload(showUpload, pendingFile); setPendingFile(null); }} disabled={!pendingFile} data-testid="upload-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!showReject} onOpenChange={() => setShowReject(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rifiuta Richiesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Motivo del rifiuto</Label>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Inserisci il motivo..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(null)}>Annulla</Button>
            <Button variant="destructive" onClick={handleReject}>Rifiuta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
